import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { parseToolResult } from "@/lib/copilot/parse-tool-result";
import type { ChoiceAnswer } from "@/types/copilot/copilot-types";

const OTHER = "__other__";

export function ChoiceCard({
  question,
  options = [],
  multiple = false,
  allowOther = false,
  respond,
  result,
}: {
  question?: string;
  options?: string[];
  multiple?: boolean;
  allowOther?: boolean;
  respond?: (answer: ChoiceAnswer) => void;
  result?: string;
}) {
  const answer = parseToolResult<ChoiceAnswer>(result);
  const [picked, setPicked] = useState<string[]>([]);
  // The "Other" text is a single optional note - no validation, no API call - so local state, not a form.
  const [otherOn, setOtherOn] = useState(false);
  const [other, setOther] = useState("");
  const disabled = !respond;
  const shown = answer?.selected ?? picked;
  const otherText = answer?.other ?? other;
  const otherShown = answer ? Boolean(answer.other) : otherOn;

  const submit = () =>
    respond?.({ selected: picked, ...(otherOn && other.trim() ? { other: other.trim() } : {}) });
  const canSubmit = picked.length > 0 || (otherOn && other.trim() !== "");

  if (!multiple && !allowOther) {
    return (
      <div className="my-2 flex flex-col gap-1.5">
        {question && <p className="text-sm">{question}</p>}
        <div className="flex flex-wrap gap-1.5">
          {options.map((option) => (
            <Button
              key={option}
              size="sm"
              variant={shown.includes(option) ? "default" : "outline"}
              disabled={disabled}
              onClick={() => respond?.({ selected: [option] })}
              className="h-auto whitespace-normal py-1 text-left text-xs"
            >
              {option}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  const otherInput = otherShown && (
    <Input
      value={otherText}
      onChange={(e) => setOther(e.target.value)}
      disabled={disabled}
      placeholder="Type your answer"
      className="h-8 text-xs"
      autoFocus={!disabled}
    />
  );

  return (
    <Card className="my-2">
      <CardContent className="flex flex-col gap-2 p-3 text-xs">
        {question && <p className="text-sm">{question}</p>}
        {multiple ? (
          <div className="flex flex-col gap-1.5">
            {options.map((option) => (
              <Label key={option} className="flex items-center gap-2 text-xs font-normal">
                <Checkbox
                  checked={shown.includes(option)}
                  disabled={disabled}
                  onCheckedChange={(on) =>
                    setPicked((prev) => (on ? [...prev, option] : prev.filter((p) => p !== option)))
                  }
                />
                {option}
              </Label>
            ))}
            {allowOther && (
              <Label className="flex items-center gap-2 text-xs font-normal">
                <Checkbox checked={otherShown} disabled={disabled} onCheckedChange={(on) => setOtherOn(on === true)} />
                Other
              </Label>
            )}
          </div>
        ) : (
          <RadioGroup
            value={otherShown ? OTHER : shown[0] ?? ""}
            disabled={disabled}
            onValueChange={(value) => {
              setOtherOn(value === OTHER);
              setPicked(value === OTHER ? [] : [value]);
            }}
            className="gap-1.5"
          >
            {[...options, OTHER].map((option) => (
              <Label key={option} className="flex items-center gap-2 text-xs font-normal">
                <RadioGroupItem value={option} />
                {option === OTHER ? "Other" : option}
              </Label>
            ))}
          </RadioGroup>
        )}
        {otherInput}
        {!answer && (
          <Button size="sm" className="self-start" disabled={disabled || !canSubmit} onClick={submit}>
            Submit
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
